/*
 * Cart Add-On Filter — PRC 07.2026 (Baked by Melissa)
 * Task: MOBILE: Sticky ATC button on PDP → cart add-on follow-up.
 *
 * Mirrors the PDP add-on filtering in `biscuit-bundler.js` (initAddOnSelectionLogic),
 * but for the Selleasy upsell tiles in the cart.
 *
 * The rule, from the client:
 *   - An add-on tied to a pack size ("Congrats Gift Box" for 25s vs the one for 50s)
 *     shows ONLY if the cart holds exactly that one pack size.
 *   - An add-on tied to no pack size (Happy Birthday Card, Birthday Candles) always
 *     shows.
 *   - A MIXED cart — more than one pack size in it — hides every pack-specific add-on.
 *     Farrah, 2026-07-23: BBM renamed the 25 and 50 gift boxes to identical titles, so
 *     in a mixed cart the shopper is offered two tiles reading "Congrats Gift Box" with
 *     no way to tell which fits which pack. Showing neither beats showing an
 *     indistinguishable pair. This is the one place the module hides on purpose.
 *
 * Why this has to be done in the theme: Selleasy can only target the PARENT bundle
 * product. Biscuit Bundles adds the bundle as a SINGLE cart line and records the
 * chosen pack in line item properties, which Selleasy's targeting never reads —
 * so it cannot vary its tiles by pack size on its own. Verified on the dev store.
 *
 * TWO DIFFERENT SIGNALS, and the asymmetry is deliberate — see PACK SIGNALS below.
 *
 * FAILS OPEN everywhere except the mixed-cart rule above. No cart data, no pack
 * detected, a parse error, an unreadable tile — every tile shows. Hiding a real add-on
 * costs BBM a sale; showing one too many is only untidy.
 *
 * ── PACK SIGNALS ──────────────────────────────────────────────────────────────────
 *
 * TILE side → the product HANDLE. Titles cannot be used: BBM deliberately made them
 * identical across pack sizes (verified on the live catalog 2026-07-27 —
 * `empty-congrats-gift-box-25-only` and `empty-congrats-gift-box-50-only` are BOTH
 * titled "Congrats Gift Box"; likewise Groovy Birthday, Sending Love, Thank You,
 * Classic Tie-Dye). A title-only rule finds a pack count in none of them and shows
 * everything — i.e. it is a silent no-op on production data, which is exactly what
 * this module shipped as before today. Handles still carry the size, in four shapes:
 *   empty-congrats-gift-box-50-only · black-birthday-gift-box-25 ·
 *   add-on-classic-tie-dye-gift-box-25-pack · pawpatrol-evg-gb25
 * Title is kept only as a fallback for tiles that expose no handle.
 *
 * CART side → the visible line title / "Step N" properties, NOT handles. Verified
 * against /cart.js on bbym-develop-2:
 *   properties["Step 1"] = "1x Latest & Greatest Cupcakes 50-Pack"
 * and the pack products themselves are still titled "… 25-Pack" / "… 50-Pack", so the
 * text carries the size. Handles must NOT be read here: a gift box already in the cart
 * has "25" in its handle, and counting that as "a 25-pack is in the cart" would make
 * every cart that holds a 50-pack plus a 25 gift box look MIXED — hiding the add-ons
 * from the shopper who just proved they want them.
 */
(function () {
  'use strict';

  var TILE = 'lb-upsell-flat-card';
  var WIDGET = 'lb-upsell-widget';
  var HIDDEN_ATTR = 'data-prc-addon-hidden';
  var PENDING_ATTR = 'data-prc-addon-filter';
  var EMPTY_ATTR = 'data-prc-addon-empty';

  // Cart containers only. Selleasy also runs PDP campaigns, and those must NOT be
  // filtered by cart contents — a PDP upsell is about the product being viewed.
  var CART_SCOPE =
    'cart-drawer, cart-drawer-items, #CartDrawer, #main-cart-items, .cart-drawer, .cart__contents, .drawer__inner';

  // Matches "25-Pack", "25 Pack", "50-Packs", "Pack of 25".
  //
  // Only the word "pack" counts. NOT "pk"/"ct"/"count": those are accessory unit
  // conventions ("Birthday Candles 24 ct"), and treating one as a pack size would
  // gate an accessory on a numeric coincidence and HIDE it — the one failure mode
  // this module must never have.
  //
  // The leading (^|[^\d]) is a consumed character rather than a lookbehind, which
  // iOS Safari below 16.4 does not support. Without it, "Graduation 2025 Pack"
  // parses as 25 (\d{1,3} backtracks onto "025").
  var PACK_PATTERNS = [
    /(?:^|[^\d])(\d{1,3})\s*[-–\s]?\s*packs?\b/gi,
    /\bpacks?\s+of\s+(\d{1,3})\b/gi,
  ];

  // The only sizes that mean anything here: cupcake pack sizes that a gift box can be
  // paired with. Any other number followed by "Pack" is a different kind of product and
  // must NOT gate anything.
  //
  // This is not defensive tidying — without it the filter HIDES a live add-on. BBM's
  // Selleasy cart campaign currently offers "Treat Yourself Cupcakes 6-Pack"
  // (`treat-yourself-6-pack`), whose title parses as pack size 6. Six is never in the
  // cart's pack list, so the tile was hidden from essentially every cart. Verified on
  // the storefront 2026-07-27 — it is one of the three tiles live right now.
  var PACK_SIZES = [25, 50, 100];

  // Pack size out of a product HANDLE. Only the sizes BBM actually sells are matched —
  // an open \d{1,3} would turn any stray number in a handle into a hide.
  //
  // Matches, from the real catalog:
  //   empty-congrats-gift-box-50-only          → 50   (bare segment)
  //   black-birthday-gift-box-25               → 25   (bare segment, terminal)
  //   add-on-classic-tie-dye-gift-box-25-pack  → 25
  //   pawpatrol-evg-gb25                       → 25   (glued to letters, no separator)
  //
  // Must NOT match:
  //   winter-2025-assortment  — the segment is all digits, so the optional [a-z]* can
  //                             only match empty and "2025" never starts with 25/50/100
  //                             at a segment boundary. This is the false-positive the
  //                             PDP filter's bare `handle.includes('25')` does hit.
  //   birthday-candles, happy-birthday-card-cupcake — no digits, always shown.
  var HANDLE_PACK_PATTERN = /(?:^|-)[a-z]*(25|50|100)(?:-|$)/g;

  // "Which packs are in the cart" and "have we ever managed to READ the cart" are two
  // different questions, and conflating them into one nullable was the bug that made
  // this module sit there showing everything. A cart with no packs in it (empty, or
  // only 6-packs) and a cart we have not read yet both produce "no pack sizes" — but
  // only the second one must keep retrying, and only the first is safe to treat as a
  // final answer and stop re-judging on.
  var packsInCart = []; // DISTINCT pack sizes; meaningful only once cartKnown
  var cartKnown = false; // a /cart.js read has succeeded at least once
  var failedReads = 0; // consecutive failures, so a dead endpoint can't be hammered
  var MAX_FAILED_READS = 3;
  var refreshing = false;
  var pendingForce = false; // a forced re-read arrived while a fetch was in flight
  var retimer = null; // deferred re-read scheduled by the throttle
  var lastFetchAt = 0;
  var MIN_FETCH_GAP = 800; // ms — the DOM observer can fire in bursts
  var FETCH_TIMEOUT = 8000; // ms — a hung request must not wedge `refreshing`
  // Widgets whose tiles have actually been judged, tracked by node identity. No
  // feature-test fallback: WeakSet long predates the custom elements and fetch that
  // Selleasy itself requires, and the DOM-residue fallback that used to stand in here
  // is no longer equivalent now that the pending attribute persists while a widget
  // waits for its tiles.
  var judged = new WeakSet();

  function packSizesFrom(text) {
    var found = [];
    if (!text) return found;
    PACK_PATTERNS.forEach(function (re) {
      re.lastIndex = 0;
      var m;
      while ((m = re.exec(text)) !== null) {
        var n = parseInt(m[1], 10);
        if (PACK_SIZES.indexOf(n) !== -1) found.push(n);
      }
    });
    return found;
  }

  function packSizesFromHandle(handle) {
    var found = [];
    if (!handle) return found;
    var h = String(handle).toLowerCase();
    HANDLE_PACK_PATTERN.lastIndex = 0;
    var m;
    while ((m = HANDLE_PACK_PATTERN.exec(h)) !== null) {
      found.push(parseInt(m[1], 10));
      // A zero-width match would spin forever; the pattern can't produce one, but the
      // guard costs nothing and this loop runs on every cart render.
      if (m.index === HANDLE_PACK_PATTERN.lastIndex) HANDLE_PACK_PATTERN.lastIndex++;
    }
    return found;
  }

  function distinct(list) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (out.indexOf(list[i]) === -1) out.push(list[i]);
    }
    return out;
  }

  function refreshCartPacks(force) {
    // A forced read that lands mid-flight must not be dropped: the in-flight
    // response predates the cart change that triggered it, so applying it would
    // judge tiles against the OLD cart and hide add-ons that now match. Remember
    // it and re-run when the current fetch settles.
    if (refreshing) {
      pendingForce = pendingForce || !!force;
      return Promise.resolve();
    }
    // Throttled: re-judge with the list we already have (so tiles never keep a
    // pre-cart-read "show everything" verdict), and queue a real re-read for when
    // the gap expires — otherwise a widget injected inside the window is judged
    // against the pre-change cart with nothing to recover it.
    if (!force && Date.now() - lastFetchAt < MIN_FETCH_GAP) {
      if (!retimer) {
        retimer = setTimeout(function () {
          retimer = null;
          refreshCartPacks(true);
        }, Math.max(0, MIN_FETCH_GAP - (Date.now() - lastFetchAt)));
      }
      apply();
      return Promise.resolve();
    }
    refreshing = true;
    // A hung request must not wedge `refreshing` true for the life of the page —
    // that would silently drop every later read, including forced ones.
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var killer = setTimeout(function () {
      if (controller) controller.abort();
      refreshing = false;
    }, FETCH_TIMEOUT);
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined,
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var sizes = [];
        (cart.items || []).forEach(function (item) {
          // The line's own product/variant title covers plain pack products.
          sizes = sizes.concat(packSizesFrom(item.title));
          sizes = sizes.concat(packSizesFrom(item.variant_title));
          // Biscuits writes the chosen pack into the visible "Step N" properties.
          var props = item.properties || {};
          Object.keys(props).forEach(function (key) {
            if (key.charAt(0) === '_') return; // internal (_bundleId, _selectedItems…)
            sizes = sizes.concat(packSizesFrom(String(props[key] || '')));
          });
        });
        // De-duplicated, because the count of DISTINCT sizes is what decides whether
        // this is a mixed cart. Two 50-packs, or a 50-pack whose title and "Step 1"
        // property both say 50, is still one pack size.
        packsInCart = distinct(sizes);
        cartKnown = true;
        failedReads = 0;
      })
      .catch(function () {
        packsInCart = []; // fail open
        failedReads++;
      })
      .then(function () {
        clearTimeout(killer);
        refreshing = false;
        // Stamped at COMPLETION, not at start: stamping at start let a slow
        // request eat most of the gap, so the next re-read was throttled away
        // while the data it would have replaced was already stale.
        lastFetchAt = Date.now();
        apply();
        if (pendingForce) {
          pendingForce = false;
          refreshCartPacks(true);
        }
      });
  }

  // Selleasy puts the handle on the tile element itself (verified on the dev store:
  // `lb-product-handle` alongside `lb-product-id` / `lb-variant-id`). The other names
  // and the product-link fallback cost nothing and mean an app update that renames the
  // attribute degrades to "show everything" rather than to a wrong answer.
  var HANDLE_ATTRS = ['lb-product-handle', 'data-lb-product-handle', 'data-product-handle', 'handle'];

  function tileHandle(tile) {
    for (var i = 0; i < HANDLE_ATTRS.length; i++) {
      var v = tile.getAttribute(HANDLE_ATTRS[i]);
      if (v) return v;
    }
    var link = tile.querySelector('a[href*="/products/"]');
    if (link) {
      var m = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
      if (m) return m[1];
    }
    return '';
  }

  function tilePackSizes(tile) {
    // Handle first — it is the only signal that still distinguishes the 25 and 50
    // gift boxes now that BBM has given them identical titles. Title is the fallback
    // for a tile that exposes no handle at all.
    var handlePacks = packSizesFromHandle(tileHandle(tile));
    if (handlePacks.length) return handlePacks;

    var titleEl = tile.querySelector('lb-card-title, .lb-card-title');
    return packSizesFrom(titleEl ? (titleEl.textContent || '').trim() : '');
  }

  function tileIsAllowed(tile) {
    var tilePacks = distinct(tilePackSizes(tile));
    if (!tilePacks.length) return true; // not pack-specific — always show

    if (!cartKnown) return true; // haven't read the cart yet — show
    if (!packsInCart.length) return true; // read it; no packs in there — show

    // Mixed cart: hide every pack-specific add-on. The tiles for different pack sizes
    // are now titled identically, so offering them side by side asks the shopper to
    // pick between two things they cannot tell apart. Client's explicit call.
    if (packsInCart.length > 1) return false;

    for (var i = 0; i < tilePacks.length; i++) {
      if (packsInCart.indexOf(tilePacks[i]) !== -1) return true;
    }
    return false;
  }

  function apply() {
    var widgets = document.querySelectorAll(WIDGET);
    for (var w = 0; w < widgets.length; w++) {
      var widget = widgets[w];
      if (!widget.closest(CART_SCOPE)) continue; // PDP campaign — leave alone

      var tiles = widget.querySelectorAll(TILE);
      var visible = 0;
      for (var t = 0; t < tiles.length; t++) {
        var tile = tiles[t];
        if (tileIsAllowed(tile)) {
          tile.removeAttribute(HIDDEN_ATTR);
          visible++;
        } else {
          tile.setAttribute(HIDDEN_ATTR, '');
        }
      }

      // Every tile hidden → hide the widget too, or Selleasy's heading and frame are
      // left standing over nothing. This went from unlikely to routine with the
      // mixed-cart rule: a campaign offering only gift boxes empties completely as
      // soon as the cart holds two pack sizes.
      if (tiles.length && visible === 0) widget.setAttribute(EMPTY_ATTR, '');
      else widget.removeAttribute(EMPTY_ATTR);

      // Release the pre-paint hold now that this widget has been judged.
      //
      // Only record it as JUDGED once the cart is actually known. Judging against an
      // unread cart is just "show everything", and marking that as judged froze the
      // widget in exactly that state for the life of the page — every later mutation
      // skipped it, so it never re-read and never filtered. Observed on the dev store:
      // tiles present, nothing hidden, no /cart.js request, permanently.
      //
      // The pending hold is still released either way: holding tiles invisible because
      // we cannot read the cart would be worse than showing all of them.
      if (tiles.length) {
        widget.removeAttribute(PENDING_ATTR);
        if (cartKnown) judged.add(widget);
      }
    }
  }

  // Mark a freshly injected widget as pending so its tiles stay hidden for the
  // frame or two before we can judge them — otherwise the shopper sees the wrong
  // add-ons flash. The attribute is only ever set by this script, so if the script
  // fails to load nothing is ever hidden.
  // Returns true if it saw a widget it hadn't judged yet.
  //
  // "Already judged" is tracked by NODE IDENTITY, not by DOM residue. Testing for
  // leftover attributes looks equivalent but isn't: apply() clears the pending
  // attribute, and a widget whose tiles all pass carries no hidden attribute
  // either — so it reads as brand new on every later mutation, blanking valid
  // tiles and firing a cart fetch each time. Dawn replaces the widget node on
  // every cart change, so genuine re-injections still register as new.
  //
  // The two conditions are deliberately SEPARATE, because Selleasy renders the widget
  // element empty and only fills it with tiles when it scrolls into view (verified on
  // the live storefront 2026-07-27 — the tiles do not exist until then). Marking
  // pending and reporting "new" used to be the same branch, so the empty widget
  // consumed the pending flag on arrival and the later tile injection reported
  // nothing new — no cart re-read, and the tiles were judged against whatever the
  // pack list happened to hold. Observed live: every tile shown, filter effectively
  // off, until something else forced a cart read.
  //
  // Pending is set once, on first sight. "New" is reported once the widget actually
  // HAS tiles — which is the moment a cart re-read is worth doing, and no earlier
  // (reporting new while it is still empty would poll /cart.js for as long as the
  // drawer stays open).
  function holdNewWidgets() {
    var sawNew = false;
    var widgets = document.querySelectorAll(WIDGET);
    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];
      if (!widget.closest(CART_SCOPE)) continue;
      if (judged.has(widget)) continue;
      if (!widget.hasAttribute(PENDING_ATTR)) widget.setAttribute(PENDING_ATTR, 'pending');
      if (widget.querySelector(TILE)) sawNew = true;
    }
    return sawNew;
  }

  // Dawn replaces <cart-drawer-items> wholesale on every cart mutation, and
  // Selleasy re-injects its widget into the new node — verified: the widget node
  // identity changes and no custom event is emitted. So we observe from <body>
  // (an observer bound to the widget or its parent dies with the replaced node)
  // and re-apply on any subtree change.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;

    // rAF for the timing (judge before paint, so no wrong add-on ever flashes), with a
    // timer racing it for the correctness.
    //
    // requestAnimationFrame does NOT fire while the tab is hidden. Since `scheduled`
    // latches until the callback runs, a single mutation arriving while hidden used to
    // swallow every mutation after it — the observer kept firing and returning early,
    // and no widget was ever judged. It recovers when the tab is shown, so a shopper
    // rarely sees it, but "correct only in a visible tab" is not a property a filter
    // that decides what gets offered should have. It also makes the module untestable
    // in any automated browser, where the tab is usually hidden: the whole thing looks
    // inert, and only a cart event (which reaches apply() without rAF) appears to work.
    //
    // Whichever fires first does the work; the other sees `ran` and returns.
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      scheduled = false;
      // A widget we haven't judged means Selleasy just (re-)injected, which in
      // practice only happens after a cart change — including ones no event tells
      // us about, e.g. the shopper tapping "Add" on a Selleasy tile itself. Re-read
      // the cart before judging, or we'd filter against a stale pack list.
      var sawNew = holdNewWidgets();
      // While the cart is still unknown, FORCE the read rather than let the throttle
      // answer with data we do not have. The throttle's "re-judge with what we already
      // have" is only sound once there is something to have; before that it just
      // repeats "show everything" and, with the pre-fix judging above, made that
      // permanent. Bounded by failedReads so a dead /cart.js cannot be hammered.
      if (!cartKnown && failedReads < MAX_FAILED_READS) refreshCartPacks(true);
      else if (sawNew) refreshCartPacks();
      else apply();
    };
    requestAnimationFrame(run);
    setTimeout(run, 50);
  }

  function start() {
    refreshCartPacks(true);
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });

    // Cart mutations change which packs are in the cart, so re-read /cart.js.
    // NOTE: Dawn declares PUB_SUB_EVENTS with `const` in constants.js, so it is a
    // global BINDING but never a property of window — `window.PUB_SUB_EVENTS` is
    // undefined and a check against it silently skips the subscription (which is
    // exactly the bug this line originally shipped with). Reference the bare
    // identifiers instead.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          refreshCartPacks(true);
        });
      }
    } catch (e) {
      /* pub/sub not present — the DOM observer above still covers us */
    }

    // Belt and braces: Dawn's pub/sub misses cart changes made by other apps.
    document.addEventListener('cart:updated', function () {
      refreshCartPacks(true);
    });
    document.addEventListener('fg-gifts:updated', function () {
      refreshCartPacks(true);
    });
  }

  // Read-only diagnostic hook. This module decides what a shopper is offered, from
  // state that is otherwise entirely invisible — the failure it shipped with looked
  // identical from the outside to "working, nothing to hide", and cost a long session
  // of guessing to pin down. `window.prcCartAddonFilter.state()` answers it directly.
  // Nothing in the module reads this back; removing it changes no behaviour.
  window.prcCartAddonFilter = {
    state: function () {
      return {
        cartKnown: cartKnown,
        packsInCart: packsInCart.slice(),
        failedReads: failedReads,
        refreshing: refreshing,
        msSinceFetch: lastFetchAt ? Date.now() - lastFetchAt : null,
      };
    },
    refresh: function () {
      return refreshCartPacks(true);
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
